import React, { useState, useEffect, useRef } from "react"
import { Container, Row, Col } from "reactstrap"
import Loader from "pages/Separate/Loader"
import html2pdf from "html2pdf.js"
import "../../../assets/css/KnowledgeBase/Index.css"

const MOCK_DOCUMENTS = [
  {
    id: 1,
    title: "Privacy Policy",
    category: "Legal",
    updated: "15 Jan 2025",
    user_access: "1,2,3",
    role_access: "",
    content: `
      <h2 style="color:#5c0385">Privacy Policy</h2>
      <p><strong>Effective Date:</strong> January 1, 2025</p>
      <hr/>
      <h4>1. Introduction</h4>
      <p>This Privacy Policy describes how Zithas Technologies collects, uses, and shares information about you when you use our services. By using our services, you agree to the collection and use of information in accordance with this policy.</p>
      <h4>2. Information We Collect</h4>
      <p>We collect information you provide directly to us, such as when you create an account, fill out a form, or contact us for support. This may include your name, email address, phone number, and any other information you choose to provide.</p>
      <h4>3. How We Use Your Information</h4>
      <ul>
        <li>To provide, maintain, and improve our services</li>
        <li>To process transactions and send related information</li>
        <li>To send technical notices and support messages</li>
        <li>To respond to your comments and questions</li>
      </ul>
      <h4>4. Data Security</h4>
      <p>We take reasonable measures to help protect information about you from loss, theft, misuse and unauthorized access, disclosure, alteration and destruction.</p>
    `,
  },
  {
    id: 2,
    title: "Development Standards",
    category: "Engineering",
    updated: "02 Mar 2025",
    user_access: "1,2",
    role_access: "3,4",
    content: `
      <h2 style="color:#5c0385">Development Standards</h2>
      <p><strong>Version:</strong> 2.0 | <strong>Last Updated:</strong> March 2025</p>
      <hr/>
      <h4>1. Code Quality</h4>
      <p>All code must follow the established coding guidelines. Code reviews are mandatory before merging into the main branch. Each pull request must include unit tests covering the new functionality.</p>
      <h4>2. Git Workflow</h4>
      <ul>
        <li>Feature branches: <code>feature/description</code></li>
        <li>Bug fix branches: <code>fix/description</code></li>
        <li>Commit messages must be descriptive and in present tense</li>
      </ul>
      <h4>3. API Standards</h4>
      <p>All APIs must be RESTful and follow the naming conventions outlined in the API documentation. Response formats must be consistent across all endpoints.</p>
      <h4>4. Testing Requirements</h4>
      <p>Minimum 80% code coverage is required for all new features. Integration tests must be written for all API endpoints.</p>
    `,
  },
  {
    id: 3,
    title: "HR Handbook",
    category: "Human Resources",
    updated: "10 Feb 2025",
    user_access: "",
    role_access: "2,3",
    content: `
      <h2 style="color:#5c0385">HR Handbook</h2>
      <p><strong>Revision:</strong> 5th Edition | <strong>Year:</strong> 2025</p>
      <hr/>
      <h4>1. Work Hours & Attendance</h4>
      <p>Standard work hours are 9:00 AM to 6:00 PM, Monday through Friday. Employees are expected to maintain regular attendance. Any planned absence must be reported to the direct manager at least 24 hours in advance.</p>
      <h4>2. Leave Policy</h4>
      <ul>
        <li><strong>Annual Leave:</strong> 18 days per year</li>
        <li><strong>Sick Leave:</strong> 12 days per year</li>
        <li><strong>Casual Leave:</strong> 6 days per year</li>
        <li><strong>Maternity/Paternity Leave:</strong> As per government regulations</li>
      </ul>
      <h4>3. Code of Conduct</h4>
      <p>All employees are expected to maintain professional behaviour, treat colleagues with respect, and uphold the company values at all times.</p>
      <h4>4. Performance Reviews</h4>
      <p>Performance reviews are conducted bi-annually in June and December. Employees will be evaluated on KPIs defined at the start of each review cycle.</p>
    `,
  },
  {
    id: 4,
    title: "Onboarding Kit",
    category: "HR / General",
    updated: "01 Jan 2025",
    user_access: "1,2,3,4",
    role_access: "1,2,3,4",
    content: `
      <h2 style="color:#5c0385">Onboarding Kit</h2>
      <p><strong>Welcome to Zithas Technologies!</strong></p>
      <hr/>
      <h4>Day 1 Checklist</h4>
      <ul>
        <li>Complete joining formalities with HR</li>
        <li>Collect your laptop and access credentials</li>
        <li>Setup development environment</li>
        <li>Meet your team and direct manager</li>
        <li>Review the company org chart</li>
      </ul>
      <h4>Week 1 Goals</h4>
      <p>During your first week you will be introduced to the projects you will be working on, attend orientation sessions, and complete all mandatory compliance training modules.</p>
      <h4>Important Contacts</h4>
      <ul>
        <li><strong>HR Team:</strong> hr@zithas.com</li>
        <li><strong>IT Support:</strong> it@zithas.com</li>
        <li><strong>Admin:</strong> admin@zithas.com</li>
      </ul>
    `,
  },
  {
    id: 5,
    title: "SOP Manual",
    category: "Operations",
    updated: "20 Apr 2025",
    user_access: "",
    role_access: "2,3,4",
    content: `
      <h2 style="color:#5c0385">Standard Operating Procedures</h2>
      <p><strong>Department:</strong> Operations | <strong>Version:</strong> 3.1</p>
      <hr/>
      <h4>1. Project Initiation</h4>
      <p>Every new project must be registered in the CRM before work begins. A project brief must be created and approved by the department head within 48 hours of client confirmation.</p>
      <h4>2. Client Communication</h4>
      <p>All client communications must be logged in the CRM. Response time SLA is 4 hours during business hours. Weekly status updates must be sent every Friday by 5:00 PM.</p>
      <h4>3. Deliverable Review</h4>
      <ul>
        <li>Internal review before sharing with the client</li>
        <li>QA sign-off required for all technical deliverables</li>
        <li>Client approval must be documented in writing</li>
      </ul>
      <h4>4. Escalation Matrix</h4>
      <p>Level 1: Direct Manager → Level 2: Department Head → Level 3: Director → Level 4: CEO</p>
    `,
  },
  {
    id: 6,
    title: "Security Guidelines",
    category: "IT / Security",
    updated: "05 May 2025",
    user_access: "1,2,3",
    role_access: "2,3,4",
    content: `
      <h2 style="color:#5c0385">Security Guidelines</h2>
      <p><strong>Classification:</strong> Confidential | <strong>Version:</strong> 1.5</p>
      <hr/>
      <h4>1. Password Policy</h4>
      <p>All passwords must be at least 12 characters and include uppercase, lowercase, numbers, and symbols. Passwords must be changed every 90 days.</p>
      <h4>2. Data Handling</h4>
      <ul>
        <li>Never share credentials over email or chat</li>
        <li>Lock your screen when stepping away</li>
        <li>Use only company-approved storage solutions</li>
      </ul>
      <h4>3. Incident Reporting</h4>
      <p>Any suspected security incident must be reported to the IT team within 1 hour of discovery.</p>
    `,
  },
]

const DOC_TABS = [
  { key: "my", label: "My Documents", icon: "bi-person-fill" },
  { key: "role", label: "Role Documents", icon: "bi-people-fill" },
]

const stripHtml = html =>
  html.replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim()

const KnowledgeDocuments = () => {
  const [activeDocTab, setActiveDocTab] = useState("my")
  const [row, setRow] = useState(1)
  const [selectedDoc, setSelectedDoc] = useState(null)
  const [documents, setDocuments] = useState([])
  const [searchTerm, setSearchTerm] = useState("")
  const [loading, setLoading] = useState(false)
  const viewerRef = useRef(null)

  document.title = "Documents | Zithas Crm"

  useEffect(() => {
    document.body.classList.add("kb-docs-page")
    return () => document.body.classList.remove("kb-docs-page")
  }, [])

  useEffect(() => {
    setLoading(true)
    setTimeout(() => {
      setDocuments(MOCK_DOCUMENTS)
      setLoading(false)
    }, 400)
  }, [])

  const handleDownloadPdf = () => {
    if (!viewerRef.current) return
    html2pdf()
      .set({
        margin: 12,
        filename: `${selectedDoc?.title || "document"}.pdf`,
        html2canvas: { scale: 2, useCORS: true },
        jsPDF: { unit: "mm", format: "a4", orientation: "portrait" },
      })
      .from(viewerRef.current)
      .save()
  }

  const openDoc = doc => { setSelectedDoc(doc); setRow(2) }
  const goBack = () => { setRow(1); setSelectedDoc(null) }

  const handleTabSwitch = tab => {
    setActiveDocTab(tab)
    setRow(1)
    setSelectedDoc(null)
    setSearchTerm("")
  }

  const authId = localStorage.getItem("auth_id")?.toString()
  const authRole = localStorage.getItem("auth_role")?.toString()

  const myDocs = documents.filter(doc =>
    doc.user_access?.split(",").map(s => s.trim()).includes(authId)
  )
  const roleDocs = documents.filter(doc =>
    doc.role_access?.split(",").map(s => s.trim()).includes(authRole)
  )

  const activeList = activeDocTab === "my" ? myDocs : roleDocs
  const filteredList = searchTerm
    ? activeList.filter(doc => doc.title?.toLowerCase().includes(searchTerm.toLowerCase()))
    : activeList

  return (
    <React.Fragment>
      {loading && <Loader />}

      <div className="py-5 my-5 kb-docs-content">
        <Container fluid>
          <div className="card" style={{ borderRadius: "10px", minHeight: "78vh", overflow: "hidden" }}>

            {/* ── Horizontal Tab Bar ── */}
            <div
              className="d-flex align-items-center justify-content-between px-4"
              style={{ borderBottom: "2px solid #f0f0f0", background: "#fff" }}
            >
              <div className="d-flex">
                {DOC_TABS.map(tab => (
                  <button
                    key={tab.key}
                    onClick={() => handleTabSwitch(tab.key)}
                    className="d-flex align-items-center gap-2"
                    style={{
                      background: "transparent",
                      border: "none",
                      borderBottom: activeDocTab === tab.key ? "3px solid #5c0385" : "3px solid transparent",
                      color: activeDocTab === tab.key ? "#5c0385" : "#888",
                      fontWeight: activeDocTab === tab.key ? 600 : 400,
                      fontSize: "13.5px",
                      padding: "14px 22px 11px",
                      cursor: "pointer",
                      transition: "all 0.2s",
                      marginBottom: "-2px",
                    }}
                  >
                    <i className={`bi ${tab.icon}`} style={{ fontSize: "14px" }} />
                    {tab.label}
                    <span
                      style={{
                        fontSize: "10px",
                        fontWeight: 700,
                        borderRadius: "20px",
                        padding: "1px 8px",
                        background: activeDocTab === tab.key ? "#5c0385" : "#eee",
                        color: activeDocTab === tab.key ? "#fff" : "#999",
                        marginLeft: "2px",
                      }}
                    >
                      {tab.key === "my" ? myDocs.length : roleDocs.length}
                    </span>
                  </button>
                ))}
              </div>

              {/* Right controls */}
              <div className="d-flex align-items-center gap-2">
                {row === 1 && (
                  <div className="position-relative">
                    <i
                      className="bi bi-search position-absolute"
                      style={{ top: "9px", left: "11px", color: "#bbb", fontSize: "13px" }}
                    />
                    <input
                      type="text"
                      className="form-control form-control-sm"
                      placeholder="Search documents..."
                      value={searchTerm}
                      onChange={e => setSearchTerm(e.target.value)}
                      style={{ paddingLeft: "30px", fontSize: "13px", width: "220px", borderRadius: "6px" }}
                    />
                  </div>
                )}
                {row === 2 && (
                  <>
                    <button
                      onClick={goBack}
                      style={{
                        background: "#f5f5f5",
                        border: "1px solid #e0e0e0",
                        borderRadius: "6px",
                        padding: "6px 14px",
                        fontSize: "13px",
                        color: "#555",
                        cursor: "pointer",
                        display: "flex",
                        alignItems: "center",
                        gap: "6px",
                      }}
                    >
                      <i className="bi bi-arrow-left" />
                      Back
                    </button>
                    <button
                      onClick={handleDownloadPdf}
                      style={{
                        background: "#5c0385",
                        color: "#fff",
                        border: "none",
                        borderRadius: "6px",
                        padding: "6px 16px",
                        fontSize: "13px",
                        cursor: "pointer",
                        display: "flex",
                        alignItems: "center",
                        gap: "6px",
                      }}
                    >
                      <i className="bi bi-download" />
                      Download PDF
                    </button>
                  </>
                )}
              </div>
            </div>

            {/* ── Card Grid (row = 1) ── */}
            {row === 1 && (
              <div className="p-4" style={{ background: "#f8f8f8", minHeight: "70vh" }}>
                {filteredList.length === 0 ? (
                  <div
                    className="d-flex flex-column align-items-center justify-content-center"
                    style={{ minHeight: "55vh" }}
                  >
                    <div
                      style={{
                        width: "90px", height: "90px", borderRadius: "50%",
                        background: "#f3e5f5", display: "flex",
                        alignItems: "center", justifyContent: "center", marginBottom: "18px",
                      }}
                    >
                      <i className="bi bi-folder2-open" style={{ fontSize: "38px", color: "#5c038555" }} />
                    </div>
                    <h6 style={{ color: "#999", fontWeight: 500 }}>No documents found</h6>
                    <p style={{ fontSize: "13px", color: "#bbb", marginBottom: 0 }}>
                      {searchTerm ? "Try a different search term" : "No documents assigned to you yet"}
                    </p>
                  </div>
                ) : (
                  <Row className="g-4">
                    {filteredList.map(doc => (
                      <Col key={doc.id} md="6">
                        <div
                          onClick={() => openDoc(doc)}
                          style={{
                            background: "#fff",
                            borderRadius: "10px",
                            border: "1px solid #ececec",
                            padding: "0",
                            cursor: "pointer",
                            transition: "all 0.2s",
                            overflow: "hidden",
                            boxShadow: "0 1px 4px rgba(0,0,0,0.04)",
                          }}
                          onMouseEnter={e => {
                            e.currentTarget.style.boxShadow = "0 6px 20px rgba(92,3,133,0.13)"
                            e.currentTarget.style.borderColor = "#5c038540"
                            e.currentTarget.style.transform = "translateY(-2px)"
                          }}
                          onMouseLeave={e => {
                            e.currentTarget.style.boxShadow = "0 1px 4px rgba(0,0,0,0.04)"
                            e.currentTarget.style.borderColor = "#ececec"
                            e.currentTarget.style.transform = "translateY(0)"
                          }}
                        >
                          {/* Coloured top strip */}
                          <div style={{ height: "4px", background: "linear-gradient(90deg, #5c0385, #9c27b0)" }} />

                          <div className="p-4">
                            {/* Header row */}
                            <div className="d-flex align-items-start justify-content-between mb-3">
                              <div className="d-flex align-items-center gap-3">
                                <div
                                  style={{
                                    width: "46px", height: "46px", borderRadius: "10px",
                                    background: "#f3e5f5", display: "flex",
                                    alignItems: "center", justifyContent: "center", flexShrink: 0,
                                  }}
                                >
                                  <i className="bi bi-file-earmark-text-fill" style={{ fontSize: "22px", color: "#5c0385" }} />
                                </div>
                                <div>
                                  <h6 className="mb-1 fw-semibold" style={{ fontSize: "14px", color: "#222", lineHeight: 1.3 }}>
                                    {doc.title}
                                  </h6>
                                  <span style={{ fontSize: "11px", color: "#aaa" }}>
                                    <i className="bi bi-tag me-1" />
                                    {doc.category}
                                  </span>
                                </div>
                              </div>
                              <span
                                style={{
                                  fontSize: "10px", fontWeight: 600,
                                  borderRadius: "20px", padding: "3px 10px",
                                  background: activeDocTab === "my" ? "#e3f2fd" : "#f3e5f5",
                                  color: activeDocTab === "my" ? "#1565c0" : "#5c0385",
                                  whiteSpace: "nowrap",
                                  flexShrink: 0,
                                }}
                              >
                                {activeDocTab === "my" ? "Direct" : "Via Role"}
                              </span>
                            </div>

                            {/* Preview text */}
                            <p
                              style={{
                                fontSize: "12.5px", color: "#777", lineHeight: 1.6,
                                marginBottom: "16px",
                                display: "-webkit-box",
                                WebkitLineClamp: 2,
                                WebkitBoxOrient: "vertical",
                                overflow: "hidden",
                              }}
                            >
                              {stripHtml(doc.content).slice(0, 160)}...
                            </p>

                            {/* Footer row */}
                            <div
                              className="d-flex align-items-center justify-content-between"
                              style={{ borderTop: "1px solid #f5f5f5", paddingTop: "12px" }}
                            >
                              <span style={{ fontSize: "11px", color: "#bbb" }}>
                                <i className="bi bi-calendar3 me-1" />
                                Updated {doc.updated}
                              </span>
                              <span
                                className="d-flex align-items-center gap-1"
                                style={{ fontSize: "12px", color: "#5c0385", fontWeight: 600 }}
                              >
                                View Document
                                <i className="bi bi-arrow-right" style={{ fontSize: "11px" }} />
                              </span>
                            </div>
                          </div>
                        </div>
                      </Col>
                    ))}
                  </Row>
                )}
              </div>
            )}

            {/* ── Document Viewer (row = 2) ── */}
            {row === 2 && selectedDoc && (
              <div style={{ background: "#f8f8f8", minHeight: "70vh" }}>
                {/* Doc title bar */}
                <div
                  className="d-flex align-items-center gap-3 px-5 py-3"
                  style={{ background: "#fff", borderBottom: "1px solid #f0f0f0" }}
                >
                  <div
                    style={{
                      width: "40px", height: "40px", borderRadius: "9px",
                      background: "#f3e5f5", display: "flex",
                      alignItems: "center", justifyContent: "center", flexShrink: 0,
                    }}
                  >
                    <i className="bi bi-file-earmark-text-fill" style={{ fontSize: "19px", color: "#5c0385" }} />
                  </div>
                  <div>
                    <h6 className="mb-0 fw-semibold" style={{ fontSize: "15px", color: "#222" }}>
                      {selectedDoc.title}
                    </h6>
                    <small style={{ fontSize: "11px", color: "#aaa" }}>
                      <i className="bi bi-tag me-1" />{selectedDoc.category}
                      &nbsp;·&nbsp;
                      <i className="bi bi-calendar3 me-1" />Updated {selectedDoc.updated}
                      &nbsp;·&nbsp;
                      <span style={{ color: activeDocTab === "my" ? "#1565c0" : "#5c0385" }}>
                        {activeDocTab === "my" ? "Direct access" : "Role access"}
                      </span>
                    </small>
                  </div>
                </div>

                {/* HTML content area */}
                <div className="px-5 py-4" style={{ overflowY: "auto", maxHeight: "65vh" }}>
                  <div
                    style={{
                      background: "#fff", borderRadius: "10px",
                      padding: "40px 48px", maxWidth: "900px",
                      margin: "0 auto", boxShadow: "0 1px 6px rgba(0,0,0,0.06)",
                    }}
                  >
                    <div
                      id="doc-content"
                      ref={viewerRef}
                      style={{ fontSize: "14px", lineHeight: "1.9", color: "#444" }}
                      dangerouslySetInnerHTML={{ __html: selectedDoc.content }}
                    />
                  </div>
                </div>
              </div>
            )}

          </div>
        </Container>
      </div>
    </React.Fragment>
  )
}

export default KnowledgeDocuments
